import { forwardRef, type ReactNode, type ButtonHTMLAttributes, type InputHTMLAttributes } from "react";
import type { LucideIcon } from "lucide-react";
import { Layers } from "lucide-react";
import { useToast } from "../stores/toast";

export function Spinner({ className = "" }: { className?: string }) {
  return (
    <div
      className={`h-6 w-6 animate-spin rounded-full border-2 border-brand-pink border-t-transparent ${className}`}
    />
  );
}

export function PageLoader() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

export function EmptyState({
  icon: Icon = Layers,
  title,
  hint,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-center">
      <div className="relative">
        <img src="/art/mascot.svg" alt="" className="float-soft h-28 w-28 opacity-70" />
        <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-surface-2 text-brand-pink">
          <Icon size={16} strokeWidth={1.75} />
        </span>
      </div>
      <p className="mt-2 font-display text-sm text-white/80">{title}</p>
      {hint && <p className="max-w-60 text-xs text-white/50">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Button({
  variant = "primary",
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "danger" | "outline" }) {
  const styles = {
    primary:
      "bg-gradient-brand-animated text-white font-semibold shadow-lg shadow-brand-pink/25 hover:shadow-xl hover:shadow-brand-pink/35 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-lg",
    ghost: "bg-white/5 text-white/90 hover:bg-white/10 active:scale-[0.98] disabled:opacity-40",
    outline:
      "border border-brand-pink/40 text-brand-pink hover:bg-brand-pink/10 hover:border-brand-pink/70 active:scale-[0.98] disabled:opacity-40",
    danger: "bg-red-500/90 text-white font-semibold hover:bg-red-500 active:scale-[0.98] disabled:opacity-40",
  };
  return (
    <button
      className={`inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm leading-normal transition-all duration-200 disabled:cursor-not-allowed ${styles[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { label?: string; hint?: string }
>(function Input({ label, hint, className = "", ...props }, ref) {
  return (
    <label className="block">
      {label && <span className="mb-1 block text-xs font-medium text-white/60">{label}</span>}
      <input
        ref={ref}
        className={`w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-base text-white placeholder-white/30 outline-none transition-colors focus:border-brand-pink/60 lg:text-sm ${className}`}
        {...props}
      />
      {hint && <span className="mt-1 block text-[10px] text-white/40">{hint}</span>}
    </label>
  );
});

export function Badge({ children, color = "pink" }: { children: ReactNode; color?: "pink" | "blue" | "green" | "amber" | "gray" | "red" }) {
  const colors = {
    pink: "bg-brand-pink/15 text-brand-pink",
    blue: "bg-brand-blue/20 text-indigo-300",
    green: "bg-emerald-500/15 text-emerald-300",
    amber: "bg-amber-500/15 text-amber-300",
    gray: "bg-white/10 text-white/60",
    red: "bg-red-500/15 text-red-300",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${colors[color]}`}>
      {children}
    </span>
  );
}

export function statusColor(status: string): "pink" | "blue" | "green" | "amber" | "gray" | "red" {
  switch (status) {
    case "OPEN":
    case "LIVE":
    case "PAID":
    case "DELIVERED":
    case "COMPLETED":
    case "VERIFIED":
      return "green";
    case "PENDING_PAYMENT":
    case "SCHEDULED":
    case "PENDING":
      return "amber";
    case "SHIPPED":
    case "PICKED":
    case "PACKED":
    case "DRAWN":
    case "ENDED":
      return "blue";
    case "CANCELLED":
    case "FORFEITED":
    case "REJECTED":
      return "red";
    default:
      return "gray";
  }
}

export function Toasts() {
  const { toasts, dismiss } = useToast();
  return (
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[90] flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <button
          key={t.id}
          onClick={() => dismiss(t.id)}
          className={`pointer-events-auto w-full max-w-sm rounded-xl px-4 py-3 text-left text-sm font-medium shadow-xl backdrop-blur-md ${
            t.kind === "success"
              ? "bg-emerald-500/90 text-white"
              : t.kind === "error"
                ? "bg-red-500/90 text-white"
                : "bg-surface-2/95 text-white border border-white/10"
          }`}
        >
          {t.message}
        </button>
      ))}
    </div>
  );
}
